import {
  getAnchoredPosition,
  PositionSettings,
} from '@primer/behaviors/dist/esm';
import {
  toggleAttribute,
  toggleExpanded,
  toggleHidden,
} from 'src/utils/dom-toggle';
import { Context, Controller } from '@hotwired/stimulus';
import { onTransitionEnd } from 'src/utils/dom-transition';

export default class PopoverController extends Controller {
  public static targets = ['popover', 'toggler'];

  public static values = {
    side: String,
    align: String,
    alignmentOffset: Number,
    anchorOffset: Number,
    modal: String,
    initialCss: String,
    delayTime: { type: Number, default: 500 },
  };

  declare protected popoverTarget: HTMLElement;
  declare protected readonly togglerTarget: HTMLButtonElement;

  declare protected sideValue: string;
  declare protected alignValue: string;
  declare protected alignmentOffsetValue: number;
  declare protected anchorOffsetValue: number;
  declare protected modalValue: string;
  declare protected initialCssValue: string;
  declare protected delayTimeValue: number;

  declare protected readonly hasSideValue: boolean;
  declare protected readonly hasAlignValue: boolean;
  declare protected readonly hasAlignmentOffsetValue: boolean;
  declare protected readonly hasAnchorOffsetValue: boolean;
  declare protected readonly hasCloseButtonTarget: boolean;
  declare protected readonly hasModalValue: boolean;
  declare protected readonly hasTogglerTarget: boolean;
  declare protected readonly hasInitialCssValue: boolean;

  protected inflated: boolean = false;
  private currentRefCount: number = 0;
  private disabledByDefault!: boolean;
  private tabIndexByDefault!: string | null;

  protected resizeRaf: undefined | number;

  protected popover!: HTMLElement;
  private toggler!: Element;

  private timers: (() => void)[] = [];

  constructor(context: Context) {
    super(context);

    this.open = this.open.bind(this);
    this.close = this.close.bind(this);

    this.onLabelKey = this.onLabelKey.bind(this);
    this.onEscape = this.onEscape.bind(this);
    this.onOutsideClick = this.onOutsideClick.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onRecalculatePosition = this.onRecalculatePosition.bind(this);

    this.onReferenceDown = this.onReferenceDown.bind(this);
    this.onReferenceDownNextFrame = this.onReferenceDownNextFrame.bind(this);
    this.onDelayedReferenceDown = this.onDelayedReferenceDown.bind(this);
    this.onReferenceUp = this.onReferenceUp.bind(this);
    this.onDelayedReferenceUp = this.onDelayedReferenceUp.bind(this);

    this.recalculatePosition = this.recalculatePosition.bind(this);
  }

  protected getPopoverSource() {
    return this.popoverTarget;
  }

  protected getAnchorSettings(): Partial<PositionSettings> {
    return {
      side: this.hasSideValue
        ? (this.sideValue as PositionSettings['side'])
        : 'inside-center',
      align: this.hasAlignValue
        ? (this.alignValue as PositionSettings['align'])
        : 'center',
      alignmentOffset: this.hasAlignmentOffsetValue
        ? this.alignmentOffsetValue
        : undefined,
      anchorOffset: this.hasAnchorOffsetValue
        ? this.anchorOffsetValue
        : undefined,
      allowOutOfBounds: false,
      // displayInViewport: true,
    };
  }

  protected get opened() {
    return this.toggler.getAttribute('aria-expanded') === 'true';
  }

  private set opened(next: boolean) {
    toggleExpanded(this.toggler, next);

    if (!this.inflated) {
      this.inflate();
      return;
    }

    if (next) {
      void this.open();
    } else {
      this.close();
    }
  }

  private set refCount(next: number) {
    if (next > 0 && !this.opened) {
      this.opened = true;
    } else if (next <= 0 && this.opened) {
      this.opened = false;
    }

    this.currentRefCount = next;
  }

  private get refCount(): number {
    return this.currentRefCount;
  }

  private get descendantTogglerOrElement() {
    return (
      this.element.querySelector('[data-popover-target="toggler"]') ??
      this.element
    );
  }

  public connect(): void {
    this.toggler = this.hasTogglerTarget
      ? this.togglerTarget
      : this.descendantTogglerOrElement;

    this.disabledByDefault = this.toggler.hasAttribute('disabled');
    this.tabIndexByDefault = this.toggler.getAttribute('tabindex');

    if (this.disabledByDefault) {
      toggleAttribute('disabled', this.toggler, false);
    }

    if (this.tabIndexByDefault !== '0') {
      this.toggler.setAttribute('tabindex', '0');
    }

    if (this.toggler instanceof HTMLLabelElement) {
      this.toggler.addEventListener('keypress', this.onLabelKey);
    }
  }

  public disconnect(): void {
    this.close(true);
    this.inflated = false;
    this.opened = false;

    if (this.popover) {
      if (this.popover !== this.getPopoverSource()) {
        this.popover.remove();
      } else {
        this.popover.removeEventListener('mouseenter', this.onReferenceUp);
        this.popover.removeEventListener('mouseleave', this.onReferenceDown);
      }
    }

    if (this.disabledByDefault) {
      toggleAttribute('disabled', this.toggler, true);
    }

    if (this.tabIndexByDefault) {
      this.toggler.setAttribute('tabindex', this.tabIndexByDefault);
    } else {
      this.toggler.removeAttribute('tabindex');
    }

    if (this.toggler instanceof HTMLLabelElement) {
      this.toggler.removeEventListener('keypress', this.onLabelKey);
    }
  }

  private onLabelKey(e: KeyboardEvent): void {
    if (e.key !== 'Enter') {
      return;
    }

    e.preventDefault();

    const referenceId = this.toggler.getAttribute('for');

    // Propagate click to inside element
    if (referenceId) {
      document.getElementById(referenceId)?.click();
    } else {
      const inner = this.toggler.querySelector(
        [
          'input:not([hidden]):not([disabled]):not([type="hidden"])',
          'textarea:not([hidden]):not([disabled])',
          'select:not([hidden]):not([disabled])',
          'button:not([hidden]):not([disabled])',
        ].join(', '),
      );

      if (inner instanceof HTMLElement) {
        inner.click();
      }
    }
  }

  public onReferenceUp(_event: Event): void {
    this.refCount += 1;
    console.debug('[popover] ref up', this.refCount);
  }

  public onDelayedReferenceUp(e: Event): void {
    let timer: ReturnType<typeof setTimeout> | undefined = undefined;

    const onDelayCancelled = () => {
      clearTimeout(timer);
    };

    const onDelayPassed = () => {
      // Turn into noop
      const index = this.timers.indexOf(onDelayCancelled);

      if (index !== -1) {
        this.timers[index] = () => {
          requestAnimationFrame(() => {
            this.onReferenceDown(e);
          });
        };

        this.onReferenceUp(e);
      } else {
        console.warn('[popover] delay has passed but no timer was left');
      }
    };

    // Remove delay if there is already a popover active
    const delay = document.querySelector('.popover.--active')
      ? 1
      : this.delayTimeValue;

    timer = setTimeout(onDelayPassed, delay);
    this.timers.push(onDelayCancelled);
  }

  public onDelayedReferenceDown(_event: Event): void {
    const cancel = this.timers.shift();
    if (cancel) {
      cancel();
    }
  }

  public onReferenceDown(_event: Event): void {
    this.refCount = Math.max(0, this.refCount - 1);
    console.debug('[popover] ref down', this.refCount);
  }

  public onReferenceDownNextFrame(_event: Event): void {
    setTimeout(() => {
      this.refCount = Math.max(0, this.refCount - 1);
      console.debug('[popover] ref down', this.refCount);
    }, 1);
  }

  public onClose(_event: Event): void {
    this.opened = false;
  }

  private onEscape(e: KeyboardEvent): void {
    if (e.key !== 'Escape') {
      return;
    }

    e.preventDefault();
    this.opened = false;
  }

  protected onOutsideClick(e: MouseEvent): void {
    if (e.target instanceof Element) {
      if (this.popover === e.target || this.popover.contains(e.target)) {
        return;
      }

      // TODO: do we want this if action is NOT defined?
      if (this.toggler === e.target || this.toggler.contains(e.target)) {
        return;
      }
    }

    this.opened = false;
  }

  private onRecalculatePosition(_event: Event): void {
    if (this.resizeRaf) {
      return;
    }

    this.resizeRaf = requestAnimationFrame(this.recalculatePosition);
  }

  protected recalculatePosition(): void {
    if (!this.opened) {
      return;
    }

    this.resizeRaf = undefined;

    const float = this.popover;
    const anchor = this.toggler;
    const { top, left } = getAnchoredPosition(
      float,
      anchor,
      this.getAnchorSettings(),
    );
    float.style.top = `${top}px`;
    float.style.left = `${left}px`;
  }

  protected inflate(): void {
    if (this.inflated) {
      return;
    }

    this.inflated = true;

    // Move to the modals container if possible
    const modalQuery =
      this.hasModalValue && this.modalValue !== 'false'
        ? this.modalValue
        : '#modals';

    const destination =
      this.element.closest(modalQuery) || document.querySelector(modalQuery);

    if (destination && (!this.hasModalValue || this.modalValue !== 'false')) {
      this.popover = this.getPopoverSource().cloneNode(true) as HTMLElement;
      destination.append(this.popover);
    } else {
      this.popover = this.getPopoverSource();
    }

    this.popover.addEventListener('mouseenter', this.onReferenceUp);
    this.popover.addEventListener('mouseleave', this.onReferenceDown);

    if (this.popover.id) {
      this.toggler.setAttribute('aria-controls', this.popover.id);
    }

    if (this.opened) {
      this.popover.getBoundingClientRect();
      void this.open();
    } else {
      this.close(true);
    }
  }

  public async open(): Promise<void> {
    console.log('[popover] open');

    // Remove all state (and force render)
    this.popover.classList.remove(
      '--inactive',
      '--active',
      '--transitioning',
      '--in',
      '--out',
    );
    this.popover.getBoundingClientRect();

    toggleHidden(this.popover, false);

    // Position correctly
    this.recalculatePosition();

    // Add initial state (and force render)
    this.popover.classList.add('--inactive');
    this.popover.getBoundingClientRect();

    // Add animation
    this.popover.classList.add('--transitioning', '--in');
    this.popover.getBoundingClientRect();

    return new Promise((resolve, reject) => {
      requestAnimationFrame(() => {
        if (!this.opened) {
          reject(new Error('Popover was closed before it opened'));
          return;
        }

        // Transition
        this.popover.classList.add('--active');

        document.addEventListener('keydown', this.onEscape);
        document.addEventListener('click', this.onOutsideClick);
        window.addEventListener('resize', this.onRecalculatePosition);

        resolve(void 0);
      });
    });
  }

  public close(skipAnimation = false) {
    /*if (
      this.toggler instanceof HTMLElement &&
      document.activeElement !== this.toggler
    ) {
      this.toggler.focus();
      this.opened = false;
      return;
    }*/

    console.log('[popover] close');

    document.removeEventListener('keydown', this.onEscape);
    document.removeEventListener('click', this.onOutsideClick);
    window.removeEventListener('resize', this.onRecalculatePosition);

    if (!this.popover) {
      return;
    }

    if (!skipAnimation && !this.popover.classList.contains('--active')) {
      skipAnimation = true;
    }

    // Enable animation (and force render)
    this.popover.classList.remove('--in');
    this.popover.classList.add('--out');
    this.popover.getBoundingClientRect();

    if (skipAnimation) {
      // if (this.element instanceof HTMLElement) {
      //   this.element.focus();
      // }

      this.popover.classList.remove(
        '--in',
        '--out',
        '--transitioning',
        '--active',
      );
      this.popover.classList.add('--inactive');

      toggleHidden(this.popover, true);
      return;
    }

    // Force render
    this.popover.getBoundingClientRect();

    requestAnimationFrame(() => {
      if (this.opened) {
        return;
      }

      // if (this.element instanceof HTMLElement) {
      //   this.element.focus();
      // }

      // Remove "leaving.from" and add "leaving.to"
      this.popover.classList.remove('--active');
      this.popover.classList.add('--inactive');

      onTransitionEnd(this.popover, 150, () => {
        if (this.opened) {
          return;
        }

        toggleHidden(this.popover, true);
      });
    });
  }
}
